import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Country, State, City } from "country-state-city";
import { updateInfoEmpresa } from "@/features/empresa/empresaThunks";

import { validarRUT, formatearRUT } from "@/utils/rutValidator";

const urlToHandle = (url, domain) => {
  if (!url) return "";
  if (!url.includes(domain)) return url.startsWith("@") ? url : `@${url}`;
  const match = url.match(new RegExp(`${domain}/[@]?([a-zA-Z0-9._-]+)`));
  return match ? `@${match[1]}` : "";
};
const handleToURL = (handle, base, includeDogSign = false) => {
  const clean = handle.trim().replace(/^@/, "");
  return `${base}${includeDogSign ? "@" : ""}${clean}`;
};
const validateSocialHandle = (value) => {
  if (!value || value.trim() === "" || value.trim() === "@") return false;
  return /^@?[a-zA-Z0-9._-]{1,50}$/.test(value.trim());
};

const CATEGORIAS_DISPONIBLES = [
  "Arte y Cultura",
  "Artista",
  "Bares y Vida Nocturna",
  "Bienestar",
  "Deporte",
  "Emprendimiento",
  "Gastronomía",
  "Hospedaje",
  "Ocio y Entretenimiento",
  "Otros",
  "Turismo",
];

const DIAS_SEMANA = [
  { id: "lunes", label: "Lunes" },
  { id: "martes", label: "Martes" },
  { id: "miercoles", label: "Miércoles" },
  { id: "jueves", label: "Jueves" },
  { id: "viernes", label: "Viernes" },
  { id: "sabado", label: "Sábado" },
  { id: "domingo", label: "Domingo" },
];

export default function EditarInformacionEmpresa() {
  const dispatch = useDispatch();
  const { infoEmpresa, isLoadingEmpresa, errorEmpresa } = useSelector(
    (state) => state.empresa
  );

  // Estados del formulario
  const [formData, setFormData] = useState({
    nombre: "",
    descripcion: "",
    alias: "",
    numeroTributario: "",
    categorias: [],
    ubicacionFisica: false,
    pais: "",
    ciudad: "",
    comuna: "",
    direccion: "",
    tipoHorario: "Siempre abierto",
    facebook: "",
    instagram: "",
    tiktok: "",
    youtube: "",
    linkedin: "",
    twitter: "",
    twitch: "",
    website: "",
    telefono1: "",
    telefono2: "",
  });

  const [horariosDetallados, setHorariosDetallados] = useState({
    lunes: { abierto: false, horaApertura: "09:00", horaCierre: "18:00" },
    martes: { abierto: false, horaApertura: "09:00", horaCierre: "18:00" },
    miercoles: { abierto: false, horaApertura: "09:00", horaCierre: "18:00" },
    jueves: { abierto: false, horaApertura: "09:00", horaCierre: "18:00" },
    viernes: { abierto: false, horaApertura: "09:00", horaCierre: "18:00" },
    sabado: { abierto: false, horaApertura: "09:00", horaCierre: "18:00" },
    domingo: { abierto: false, horaApertura: "09:00", horaCierre: "18:00" },
  });

  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [selectedState, setSelectedState] = useState(null);

  const [loading, setLoading] = useState(false);
  const [mensaje, setMensaje] = useState({ tipo: "", texto: "" });
  const [rutError, setRutError] = useState("");

  // Cargar datos de la empresa al iniciar
  useEffect(() => {
    if (infoEmpresa) {
      setFormData({
        nombre: infoEmpresa.nombre || "",
        descripcion: infoEmpresa.descripcion || "",
        alias: infoEmpresa.alias || "",
        numeroTributario: infoEmpresa.numeroTributario || "",
        categorias: infoEmpresa.categorias || [],
        ubicacionFisica: infoEmpresa.ubicacionFisica || false,
        pais: infoEmpresa.pais || "",
        ciudad: infoEmpresa.ciudad || "",
        comuna: infoEmpresa.comuna || "",
        direccion: infoEmpresa.direccion || "",
        tipoHorario: infoEmpresa.tipoHorario || "Siempre abierto",
        facebook: urlToHandle(infoEmpresa.facebook, "facebook.com"),
        instagram: urlToHandle(infoEmpresa.instagram, "instagram.com"),
        tiktok: urlToHandle(infoEmpresa.tiktok, "tiktok.com"),
        youtube: infoEmpresa.youtube || "",
        linkedin: infoEmpresa.linkedin || "",
        twitter: infoEmpresa.twitter || "",
        twitch: infoEmpresa.twitch || "",
        website: infoEmpresa.website || "",
        telefono1: infoEmpresa.telefono1 || "",
        telefono2: infoEmpresa.telefono2 || "",
      });

      // Cargar horarios si existen
      if (infoEmpresa.horarios) {
        try {
          const horariosObj =
            typeof infoEmpresa.horarios === "string"
              ? JSON.parse(infoEmpresa.horarios)
              : infoEmpresa.horarios;
          setHorariosDetallados(horariosObj);
        } catch (error) {
          console.error("Error parseando horarios:", error);
        }
      }
    }
  }, [infoEmpresa]);

  // Cargar países
  useEffect(() => {
    const allCountries = Country.getAllCountries();
    // Próximos: 'GT', 'MX', 'AR', 'PE', 'CO'
    const allowedCountryCodes = ['CL'];
    const filteredCountries = allCountries.filter(country => 
      allowedCountryCodes.includes(country.isoCode)
    );
    setCountries(filteredCountries);

    // Si hay país guardado, cargarlo y cargar sus regiones automáticamente
    if (formData.pais && filteredCountries.length > 0) {
      const country = filteredCountries.find((c) => c.name === formData.pais);
      if (country) {
        setSelectedCountry(country);
        const countryStates = State.getStatesOfCountry(country.isoCode);
        setStates(countryStates);

        // Si hay estado/región guardada, cargarla y cargar ciudades
        if (formData.ciudad) {
          const state = countryStates.find((s) => s.name === formData.ciudad);
          if (state) {
            setSelectedState(state);
            const stateCities = City.getCitiesOfState(
              country.isoCode,
              state.isoCode
            );
            setCities(stateCities);
          }
        }
      }
    }
  }, [formData.pais, formData.ciudad]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    // Validación especial para RUT
    if (name === "numeroTributario") {
      // Permitir solo números, K y caracteres de formato
      const rutLimpio = value.replace(/[^0-9Kk]/g, "");
      
      if (rutLimpio.length <= 10) {
        const rutFormateado = formatearRUT(rutLimpio);
        setFormData({ ...formData, [name]: rutFormateado });
        
        // Validar RUT si tiene longitud suficiente
        if (rutLimpio.length >= 8) {
          if (validarRUT(rutFormateado)) {
            setRutError("");
          } else {
            setRutError("RUT inválido");
          }
        } else {
          setRutError("");
        }
      }
    } else if (["instagram", "facebook", "tiktok"].includes(name)) {
      let v = value;
      if (v && !v.startsWith("@")) v = "@" + v.replace(/^@+/, "");
      setFormData({ ...formData, [name]: v });
    } else {
      setFormData({
        ...formData,
        [name]: type === "checkbox" ? checked : value,
      });
    }
  };

  const handleCategoriasChange = (e) => {
    const options = e.target.options;
    const selected = [];
    for (let i = 0; i < options.length; i++) {
      if (options[i].selected) {
        selected.push(options[i].value);
      }
    }
    setFormData({ ...formData, categorias: selected });
  };

  const handleCountryChange = (e) => {
    const countryName = e.target.value;
    const country = countries.find((c) => c.name === countryName);

    setSelectedCountry(country);
    setSelectedState(null);
    setFormData({
      ...formData,
      pais: countryName,
      ciudad: "",
      comuna: "",
    });

    if (country) {
      const countryStates = State.getStatesOfCountry(country.isoCode);
      setStates(countryStates);
      setCities([]);
    } else {
      setStates([]);
      setCities([]);
    }
  };

  const handleStateChange = (e) => {
    const stateName = e.target.value;
    const state = states.find((s) => s.name === stateName);

    setSelectedState(state);
    setFormData({
      ...formData,
      ciudad: stateName,
      comuna: "",
    });

    if (state && selectedCountry) {
      const stateCities = City.getCitiesOfState(
        selectedCountry.isoCode,
        state.isoCode
      );
      setCities(stateCities);
    } else {
      setCities([]);
    }
  };

  const handleCityChange = (e) => {
    setFormData({
      ...formData,
      comuna: e.target.value,
    });
  };

  const toggleDia = (diaId) => {
    setHorariosDetallados({
      ...horariosDetallados,
      [diaId]: {
        ...horariosDetallados[diaId],
        abierto: !horariosDetallados[diaId].abierto,
      },
    });
  };

  const handleHorarioChange = (diaId, campo, valor) => {
    setHorariosDetallados({
      ...horariosDetallados,
      [diaId]: {
        ...horariosDetallados[diaId],
        [campo]: valor,
      },
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMensaje({ tipo: "", texto: "" });

    // Validaciones
    if (!formData.nombre || !formData.descripcion) {
      setMensaje({
        tipo: "error",
        texto: "El nombre y la descripción son obligatorios",
      });
      setLoading(false);
      return;
    }

    if (!formData.pais || !formData.ciudad) {
      setMensaje({
        tipo: "error",
        texto: "El país y la región/ciudad son obligatorios",
      });
      setLoading(false);
      return;
    }

    // Validar RUT si está presente
    if (formData.numeroTributario && !validarRUT(formData.numeroTributario)) {
      setMensaje({
        tipo: "error",
        texto: "El RUT ingresado no es válido",
      });
      setLoading(false);
      return;
    }

    // Preparar datos para enviar
    const socialData = {
      instagram: validateSocialHandle(formData.instagram) ? handleToURL(formData.instagram, "https://www.instagram.com/") : "",
      facebook: validateSocialHandle(formData.facebook) ? handleToURL(formData.facebook, "https://www.facebook.com/") : "",
      tiktok: validateSocialHandle(formData.tiktok) ? handleToURL(formData.tiktok, "https://www.tiktok.com/@") : "",
    };
    const dataToSend = {
      ...formData,
      ...socialData,
      horarios:
        formData.tipoHorario === "Horario concreto"
          ? JSON.stringify(horariosDetallados)
          : null,
    };

    try {
      await dispatch(
        updateInfoEmpresa({
          empresaUid: infoEmpresa.uid,
          formData: dataToSend,
        })
      ).unwrap();

      setMensaje({
        tipo: "exito",
        texto: "Información actualizada exitosamente",
      });

      // Limpiar mensaje después de 3 segundos
      setTimeout(() => {
        setMensaje({ tipo: "", texto: "" });
      }, 3000);
    } catch (error) {
      setMensaje({
        tipo: "error",
        texto: error || "Error al actualizar la información",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-aw-bluedark-95 rounded-lg p-6 min-h-[400px]">
      <div className="max-w-4xl mx-auto">
        <div className="mb-6">
          <h3 className="text-2xl font-semibold text-white mb-2">
            Información de la Empresa
          </h3>
          <p className="text-gray-400 text-sm">
            Mantén actualizada la información de tu empresa para que tus clientes te conozcan mejor
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Sección 1: Datos Generales */}
          <div className="bg-aw-bluedark rounded-lg p-6">
            <h4 className="text-xl font-semibold text-white mb-4 pb-2 border-b border-gray-700">
              <i className="fa-solid fa-building mr-2"></i>
              Datos Generales
            </h4>
            
            <div className="grid grid-cols-1 gap-4">
              {/* Nombre */}
              <div>
                <label htmlFor="nombre" className="block text-sm font-medium text-gray-300 mb-2">
                  Nombre de la Empresa <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="nombre"
                  name="nombre"
                  value={formData.nombre}
                  onChange={handleChange}
                  className="w-full px-4 py-2 bg-aw-blueblack border border-gray-600 rounded-lg text-white focus:outline-none focus:border-aw-green"
                  required
                />
              </div>

              {/* Descripción */}
              <div>
                <label htmlFor="descripcion" className="block text-sm font-medium text-gray-300 mb-2">
                  Descripción <span className="text-red-500">*</span>
                </label>
                <textarea
                  id="descripcion"
                  name="descripcion"
                  value={formData.descripcion}
                  onChange={handleChange}
                  className="w-full px-4 py-2 bg-aw-blueblack border border-gray-600 rounded-lg text-white focus:outline-none focus:border-aw-green resize-none"
                  rows="4"
                  required
                />
              </div>

              {/* Alias de empresa */}
              <div>
                <label htmlFor="alias" className="block text-sm font-medium text-gray-300 mb-2">
                  Alias de empresa (URL personalizada)
                </label>
                <input
                  type="text"
                  id="alias"
                  name="alias"
                  value={formData.alias}
                  onChange={handleChange}
                  className={`w-full px-4 py-2 bg-aw-blueblack border ${
                    formData.alias && !/^[a-zA-Z0-9][a-zA-Z0-9_-]{2,49}$/.test(formData.alias)
                      ? 'border-red-500'
                      : formData.alias && /^[a-zA-Z0-9][a-zA-Z0-9_-]{2,49}$/.test(formData.alias)
                      ? 'border-aw-green'
                      : 'border-gray-600'
                  } rounded-lg text-white focus:outline-none focus:border-aw-green`}
                  placeholder="ejemplo: club-aura"
                  minLength={3}
                  maxLength={50}
                />
                {formData.alias && !/^[a-zA-Z0-9][a-zA-Z0-9_-]{2,49}$/.test(formData.alias) && (
                  <p className="text-red-400 text-xs mt-1">
                    El alias debe tener 3-50 caracteres, comenzar con letra o número, y solo usar letras, números, guiones (-) o guiones bajos (_).
                  </p>
                )}
                {formData.alias && /^[a-zA-Z0-9][a-zA-Z0-9_-]{2,49}$/.test(formData.alias) && (
                  <p className="text-aw-green text-xs mt-1">
                    Tu empresa estara disponible en: /empresa/{formData.alias}
                  </p>
                )}
                {!formData.alias && (
                  <p className="text-gray-500 text-xs mt-1">
                    Opcional. Permite usar una URL personalizada para tu empresa.
                  </p>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* RUT */}
                <div>
                  <label htmlFor="numeroTributario" className="block text-sm font-medium text-gray-300 mb-2">
                    RUT de la Empresa
                  </label>
                  <input
                    type="text"
                    id="numeroTributario"
                    name="numeroTributario"
                    value={formData.numeroTributario}
                    onChange={handleChange}
                    className={`w-full px-4 py-2 bg-aw-blueblack border ${
                      rutError ? "border-red-500" : "border-gray-600"
                    } rounded-lg text-white focus:outline-none focus:border-aw-green`}
                    placeholder="12.345.678-9"
                  />
                  {rutError && (
                    <p className="text-red-500 text-xs mt-1">{rutError}</p>
                  )}
                </div>

                {/* Categorías */}
                <div>
                  <label htmlFor="categorias" className="block text-sm font-medium text-gray-300 mb-2">
                    Categorías (Mantén presionado Ctrl/Cmd para múltiples)
                  </label>
                  <select
                    id="categorias"
                    name="categorias"
                    multiple
                    value={formData.categorias}
                    onChange={handleCategoriasChange}
                    className="w-full px-4 py-2 bg-aw-blueblack border border-gray-600 rounded-lg text-white focus:outline-none focus:border-aw-green"
                    size="6"
                  >
                    {CATEGORIAS_DISPONIBLES.map((cat) => (
                      <option key={cat} value={cat}>
                        {cat.replace(/([A-Z])/g, " $1").trim()}
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-400 mt-1">
                    Seleccionadas: {formData.categorias.join(", ") || "Ninguna"}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Sección 2: Ubicación */}
          <div className="bg-aw-bluedark rounded-lg p-6">
            <h4 className="text-xl font-semibold text-white mb-4 pb-2 border-b border-gray-700">
              <i className="fa-solid fa-map-marker-alt mr-2"></i>
              Ubicación
            </h4>
            
            <div className="space-y-4">
              {/* Checkbox ubicación física */}
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="ubicacionFisica"
                  name="ubicacionFisica"
                  checked={formData.ubicacionFisica}
                  onChange={handleChange}
                  className="w-4 h-4 text-aw-green bg-aw-blueblack border-gray-600 rounded focus:ring-aw-green focus:ring-2"
                />
                <label htmlFor="ubicacionFisica" className="ml-2 text-sm font-medium text-gray-300">
                  Tiene ubicación física
                </label>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* País */}
                <div>
                  <label htmlFor="pais" className="block text-sm font-medium text-gray-300 mb-2">
                    País <span className="text-red-500">*</span>
                  </label>
                  <select
                    id="pais"
                    name="pais"
                    value={formData.pais}
                    onChange={handleCountryChange}
                    className="w-full px-4 py-2 bg-aw-blueblack border border-gray-600 rounded-lg text-white focus:outline-none focus:border-aw-green"
                    required
                  >
                    <option value="">Selecciona un país</option>
                    {countries.map((country) => (
                      <option key={country.isoCode} value={country.name}>
                        {country.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Región/Estado */}
                <div>
                  <label htmlFor="ciudad" className="block text-sm font-medium text-gray-300 mb-2">
                    Región/Estado <span className="text-red-500">*</span>
                  </label>
                  <select
                    id="ciudad"
                    name="ciudad"
                    value={formData.ciudad}
                    onChange={handleStateChange}
                    onFocus={(e) => {
                      if (!selectedCountry) {
                        e.target.blur();
                        alert('Por favor, selecciona un país primero');
                      }
                    }}
                    className="w-full px-4 py-2 bg-aw-blueblack border border-gray-600 rounded-lg text-white focus:outline-none focus:border-aw-green"
                    disabled={!selectedCountry}
                    required
                  >
                    <option value="">Selecciona una región</option>
                    {states.map((state) => (
                      <option key={state.isoCode} value={state.name}>
                        {state.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Ciudad/Comuna */}
                <div>
                  <label htmlFor="comuna" className="block text-sm font-medium text-gray-300 mb-2">
                    Ciudad/Comuna
                  </label>
                  <select
                    id="comuna"
                    name="comuna"
                    value={formData.comuna}
                    onChange={handleCityChange}
                    onFocus={(e) => {
                      if (!selectedState) {
                        e.target.blur();
                        alert('Por favor, selecciona una región primero');
                      }
                    }}
                    className="w-full px-4 py-2 bg-aw-blueblack border border-gray-600 rounded-lg text-white focus:outline-none focus:border-aw-green"
                    disabled={!selectedState}
                  >
                    <option value="">Selecciona una ciudad</option>
                    {cities.map((city) => (
                      <option key={city.name} value={city.name}>
                        {city.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Dirección */}
              <div>
                <label htmlFor="direccion" className="block text-sm font-medium text-gray-300 mb-2">
                  Dirección
                </label>
                <input
                  type="text"
                  id="direccion"
                  name="direccion"
                  value={formData.direccion}
                  onChange={handleChange}
                  className="w-full px-4 py-2 bg-aw-blueblack border border-gray-600 rounded-lg text-white focus:outline-none focus:border-aw-green"
                  placeholder="Calle, número, etc."
                />
              </div>
            </div>
          </div>

          {/* Sección 3: Horarios */}
          <div className="bg-aw-bluedark rounded-lg p-6">
            <h4 className="text-xl font-semibold text-white mb-4 pb-2 border-b border-gray-700">
              <i className="fa-solid fa-clock mr-2"></i>
              Horarios
            </h4>
            
            <div className="space-y-4">
              {/* Tipo de Horario */}
              <div>
                <label htmlFor="tipoHorario" className="block text-sm font-medium text-gray-300 mb-2">
                  Tipo de Horario
                </label>
                <select
                  id="tipoHorario"
                  name="tipoHorario"
                  value={formData.tipoHorario}
                  onChange={handleChange}
                  className="w-full px-4 py-2 bg-aw-blueblack border border-gray-600 rounded-lg text-white focus:outline-none focus:border-aw-green"
                >
                  <option value="Siempre abierto">Siempre abierto</option>
                  <option value="Horario concreto">Horario concreto</option>
                </select>
              </div>

              {/* Horarios Detallados */}
              {formData.tipoHorario === "Horario concreto" && (
                <div className="mt-4 space-y-3">
                  <p className="text-sm text-gray-400">
                    Selecciona los días que abres y define los horarios:
                  </p>
                  {DIAS_SEMANA.map((dia) => (
                    <div key={dia.id} className="bg-aw-blueblack rounded-lg p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center">
                          <input
                            type="checkbox"
                            id={`dia-${dia.id}`}
                            checked={horariosDetallados[dia.id].abierto}
                            onChange={() => toggleDia(dia.id)}
                            className="w-4 h-4 text-aw-green bg-aw-bluedark border-gray-600 rounded focus:ring-aw-green focus:ring-2"
                          />
                          <label htmlFor={`dia-${dia.id}`} className="ml-3 text-sm font-medium text-white">
                            {dia.label}
                          </label>
                        </div>

                        {horariosDetallados[dia.id].abierto && (
                          <div className="flex items-center space-x-3">
                            <div className="flex items-center">
                              <span className="text-xs text-gray-300 mr-2">Apertura:</span>
                              <input
                                type="time"
                                value={horariosDetallados[dia.id].horaApertura}
                                onChange={(e) =>
                                  handleHorarioChange(
                                    dia.id,
                                    "horaApertura",
                                    e.target.value
                                  )
                                }
                                className="px-2 py-1 bg-aw-green/95 border border-gray-600 rounded text-aw-bluedark text-sm focus:outline-none focus:border-aw-green "
                              />
                            </div>
                            <div className="flex items-center">
                              <span className="text-xs text-gray-300 mr-2">Cierre:</span>
                              <input
                                type="time"
                                value={horariosDetallados[dia.id].horaCierre}
                                onChange={(e) =>
                                  handleHorarioChange(
                                    dia.id,
                                    "horaCierre",
                                    e.target.value
                                  )
                                }
                                className="px-2 py-1 bg-aw-green/95 border border-gray-600 rounded text-aw-bluedark text-sm focus:outline-none focus:border-aw-green"
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Sección 4: Redes Sociales y Contacto */}
          <div className="bg-aw-bluedark rounded-lg p-6">
            <h4 className="text-xl font-semibold text-white mb-4 pb-2 border-b border-gray-700">
              <i className="fa-solid fa-share-nodes mr-2"></i>
              Redes Sociales y Contacto
            </h4>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Teléfono 1 */}
              <div>
                <label htmlFor="telefono1" className="block text-sm font-medium text-gray-300 mb-2">
                  <i className="fa-solid fa-phone mr-2"></i>WhatsApp
                </label>
                <span className="text-gray-400 text-xs mb-2 block">Al llenar este campo, aceptas inmediatamente que se muestre tu número de WhatsApp en tu perfil público</span>
                <input
                  type="tel"
                  id="telefono1"
                  name="telefono1"
                  value={formData.telefono1}
                  onChange={handleChange}
                  className="w-full px-4 py-2 bg-aw-blueblack border border-gray-600 rounded-lg text-white focus:outline-none focus:border-aw-green"
                  placeholder="+56 9 1234 5678"
                />
              </div>

              {/* Teléfono 2 */}
              <div>
                <label htmlFor="telefono2" className="block text-sm font-medium text-gray-300 mb-2">
                  <i className="fa-solid fa-phone mr-2"></i>Teléfono 2
                </label>
                <input
                  type="tel"
                  id="telefono2"
                  name="telefono2"
                  value={formData.telefono2}
                  onChange={handleChange}
                  className="w-full px-4 py-2 bg-aw-blueblack border border-gray-600 rounded-lg text-white focus:outline-none focus:border-aw-green"
                  placeholder="+56 9 8765 4321"
                />
              </div>

              {/* Facebook */}
              <div>
                <label htmlFor="facebook" className="block text-sm font-medium text-gray-300 mb-2">
                  <i className="fab fa-facebook mr-2"></i>Facebook
                </label>
                <input
                  type="text"
                  id="facebook"
                  name="facebook"
                  value={formData.facebook}
                  onChange={handleChange}
                  className="w-full px-4 py-2 bg-aw-blueblack border border-gray-600 rounded-lg text-white focus:outline-none focus:border-aw-green"
                  placeholder="@tu-usuario"
                />
              </div>

              {/* Instagram */}
              <div>
                <label htmlFor="instagram" className="block text-sm font-medium text-gray-300 mb-2">
                  <i className="fab fa-instagram mr-2"></i>Instagram
                </label>
                <input
                  type="text"
                  id="instagram"
                  name="instagram"
                  value={formData.instagram}
                  onChange={handleChange}
                  className="w-full px-4 py-2 bg-aw-blueblack border border-gray-600 rounded-lg text-white focus:outline-none focus:border-aw-green"
                  placeholder="@tu-usuario"
                />
              </div>

              {/* TikTok */}
              <div>
                <label htmlFor="tiktok" className="block text-sm font-medium text-gray-300 mb-2">
                  <i className="fab fa-tiktok mr-2"></i>TikTok
                </label>
                <input
                  type="text"
                  id="tiktok"
                  name="tiktok"
                  value={formData.tiktok}
                  onChange={handleChange}
                  className="w-full px-4 py-2 bg-aw-blueblack border border-gray-600 rounded-lg text-white focus:outline-none focus:border-aw-green"
                  placeholder="@tu-usuario"
                />
              </div>

              {/* YouTube */}
              <div>
                <label htmlFor="youtube" className="block text-sm font-medium text-gray-300 mb-2">
                  <i className="fab fa-youtube mr-2"></i>YouTube
                </label>
                <input
                  type="url"
                  id="youtube"
                  name="youtube"
                  value={formData.youtube}
                  onChange={handleChange}
                  className="w-full px-4 py-2 bg-aw-blueblack border border-gray-600 rounded-lg text-white focus:outline-none focus:border-aw-green"
                  placeholder="https://youtube.com/@tu-canal"
                />
              </div>

              {/* LinkedIn */}
              <div>
                <label htmlFor="linkedin" className="block text-sm font-medium text-gray-300 mb-2">
                  <i className="fab fa-linkedin mr-2"></i>LinkedIn
                </label>
                <input
                  type="url"
                  id="linkedin"
                  name="linkedin"
                  value={formData.linkedin}
                  onChange={handleChange}
                  className="w-full px-4 py-2 bg-aw-blueblack border border-gray-600 rounded-lg text-white focus:outline-none focus:border-aw-green"
                  placeholder="https://linkedin.com/company/tu-empresa"
                />
              </div>

              {/* Twitter/X */}
              <div>
                <label htmlFor="twitter" className="block text-sm font-medium text-gray-300 mb-2">
                  <i className="fab fa-x-twitter mr-2"></i>Twitter / X
                </label>
                <input
                  type="url"
                  id="twitter"
                  name="twitter"
                  value={formData.twitter}
                  onChange={handleChange}
                  className="w-full px-4 py-2 bg-aw-blueblack border border-gray-600 rounded-lg text-white focus:outline-none focus:border-aw-green"
                  placeholder="https://x.com/tu-usuario"
                />
              </div>

              {/* Twitch */}
              <div>
                <label htmlFor="twitch" className="block text-sm font-medium text-gray-300 mb-2">
                  <i className="fab fa-twitch mr-2"></i>Twitch
                </label>
                <input
                  type="url"
                  id="twitch"
                  name="twitch"
                  value={formData.twitch}
                  onChange={handleChange}
                  className="w-full px-4 py-2 bg-aw-blueblack border border-gray-600 rounded-lg text-white focus:outline-none focus:border-aw-green"
                  placeholder="https://twitch.tv/tu-canal"
                />
              </div>

              {/* Sitio Web */}
              <div>
                <label htmlFor="website" className="block text-sm font-medium text-gray-300 mb-2">
                  <i className="fas fa-globe mr-2"></i>Sitio Web
                </label>
                <input
                  type="url"
                  id="website"
                  name="website"
                  value={formData.website}
                  onChange={handleChange}
                  className="w-full px-4 py-2 bg-aw-blueblack border border-gray-600 rounded-lg text-white focus:outline-none focus:border-aw-green"
                  placeholder="https://tu-sitio-web.com"
                />
              </div>
            </div>
          </div>

          {/* Mensajes */}
          {mensaje.texto && (
            <div className={`rounded-lg p-4 ${
              mensaje.tipo === "exito" 
                ? "bg-green-500/20 border border-green-500" 
                : "bg-red-500/20 border border-red-500"
            }`}>
              <p className={`text-center font-medium ${
                mensaje.tipo === "exito" ? "text-green-400" : "text-red-400"
              }`}>
                {mensaje.texto}
              </p>
            </div>
          )}

          {/* Botón de envío */}
          <div className="flex justify-center">
            <button
              type="submit"
              disabled={loading || isLoadingEmpresa || rutError}
              className={`px-8 py-3 rounded-lg font-semibold text-lg transition-all ${
                loading || isLoadingEmpresa || rutError
                  ? "bg-gray-600 text-gray-400 cursor-not-allowed"
                  : "bg-aw-green text-aw-bluedark hover:scale-105"
              }`}
            >
              {loading || isLoadingEmpresa ? (
                <>
                  <i className="fas fa-spinner fa-spin mr-2"></i>
                  Guardando...
                </>
              ) : (
                <>
                  <i className="fas fa-save mr-2"></i>
                  Guardar Cambios
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
